import { Doc, Error as ShareDBError } from "sharedb";

import { cloneDeep, groupBy, throttle } from "lodash";
import { TLRecord, TLShape, TLStore } from "tldraw";

export type ShareDbOp = { p: string[]; oi?: TLRecord; od?: TLRecord };
export type ThrottledDoc = Doc & {
  submitImmediate: Doc["submitOp"];
};
export type ThrottledTLStore = TLStore & {
  mergeChanges: (put: TLRecord[], remove: TLRecord["id"][]) => void;
};

function reducePointsForShape(op: ShareDbOp, debug: boolean) {
  const shape = op.oi as TLShape;
  // For freehand drawings, reduce the number of points, so we don't send so much data
  if (shape?.type === "draw") {
    const opCopy = cloneDeep(op);
    // @ts-ignore
    const points = shape.props.segments[0].points;
    const filteredPoints = points.filter((_: unknown, index: number) => {
      // The more points there are in a segment, the safer it is to remove more points
      let reducePoints = 3;
      if (points.length < 100) {
        reducePoints = 1;
      } else if (points.length < 250) {
        reducePoints = 2;
      }
      return index % reducePoints === 0 || index === points.length - 1;
    });
    if (debug) {
      window.flightLogger.log(
        `[throttleShareDbDoc] Reduced points from ${points.length} to ${filteredPoints.length}`,
      );
    }
    // @ts-ignore
    opCopy.oi.props.segments[0].points = filteredPoints;
    return opCopy;
  }
  return op;
}

export const throttleShareDbDoc = (doc: Doc, throttlePeriodMs: number) => {
  let changes: ShareDbOp[] = [];
  const throttledDoc = doc as ThrottledDoc;
  throttledDoc.submitImmediate = doc.submitOp;

  // For testing
  const urlParams = new URLSearchParams(window.location.search);
  const debug = urlParams.has("debug");

  const throttledSubmit = throttle(
    () => {
      if (changes.length > 0) {
        const numberOfChanges = changes.length;
        const changesGroupedByShapeId = groupBy(changes, (change) => change.p[1]);
        const filteredChanges: ShareDbOp[] = [];
        Object.values(changesGroupedByShapeId).forEach((changesForShapeId) => {
          // Always include the first change
          filteredChanges.push(reducePointsForShape(changesForShapeId[0], debug));
          // And the last change
          if (changesForShapeId.length > 1) {
            filteredChanges.push(
              reducePointsForShape(changesForShapeId[changesForShapeId.length - 1], debug),
            );
          }
        });

        if (debug) {
          window.flightLogger.log("[throttleShareDbDoc] Ops:", filteredChanges);
          window.flightLogger.log(
            "[throttleShareDbDoc] Size of ops:",
            new Blob([JSON.stringify(filteredChanges)]).size,
          );
        }

        throttledDoc.submitImmediate(filteredChanges, {}, (err: ShareDBError) => {
          if (err) {
            window.flightLogger.error(
              "[throttleShareDbDoc] Error in ShareDBOpsThrottler when submitting ops",
              err,
            );
          } else if (debug) {
            window.flightLogger.log(
              `[throttleShareDbDoc] Successfully submitted ${filteredChanges.length} ops (${
                numberOfChanges - filteredChanges.length
              } omitted)`,
            );
          }
        });
        changes = [];
      }
    },
    throttlePeriodMs,
    { leading: true, trailing: true },
  );

  throttledDoc.submitOp = (ops: ShareDbOp[]) => {
    changes = changes.concat(ops);
    throttledSubmit();
  };

  return throttledDoc;
};

export const throttleTldrawStore = (store: TLStore, throttlePeriodMs: number) => {
  let toPut: TLRecord[] = [];
  let toRemove: TLRecord["id"][] = [];
  const throttledStore = store as ThrottledTLStore;

  // For testing
  const urlParams = new URLSearchParams(window.location.search);
  const debug = urlParams.has("debug");

  const throttledMergeChanges = throttle(
    () => {
      if (debug) {
        window.flightLogger.log(
          `[throttleTldrawStore] Merging ${toPut.length} put ops and ${toRemove.length} remove ops`,
        );
      }
      throttledStore.mergeRemoteChanges(() => {
        if (toPut.length) {
          throttledStore.put(toPut);
        }
        if (toRemove.length) {
          throttledStore.remove(toRemove);
        }
      });
      toPut = [];
      toRemove = [];
    },
    throttlePeriodMs,
    { leading: true, trailing: true },
  );

  throttledStore.mergeChanges = (put: TLRecord[], remove: TLRecord["id"][]) => {
    toPut = toPut.concat(put);
    toRemove = toRemove.concat(remove);
    throttledMergeChanges();
  };

  return throttledStore;
};
